import { AutoComplete, Button, Input, Modal } from "@arco-design/web-react";
import { useEffect, useState } from "react";

import { formatSysStaffOptionLabel } from "../service/sysDeptUserService";
import type { SysStaffItem, SysStaffSupervisorItem, SysStaffSupervisorMutationItem } from "../types";
import "./SysDeptUserPage.css";

type SysStaffSupervisorsModalProps = {
  state: { staff: SysStaffItem } | null;
  availableStaffOptions: SysStaffItem[];
  errorMessage: string;
  onClose: () => void;
  onClearError: () => void;
  onLoadSupervisors: (userId: number) => Promise<SysStaffSupervisorItem[]>;
  onSaveSupervisors: (userId: number, payload: SysStaffSupervisorMutationItem[]) => Promise<void>;
};

/** 员工领导维护弹窗。 */
export function SysStaffSupervisorsModal({
  state,
  availableStaffOptions,
  errorMessage,
  onClose,
  onClearError,
  onLoadSupervisors,
  onSaveSupervisors,
}: SysStaffSupervisorsModalProps) {
  const [items, setItems] = useState<SysStaffSupervisorMutationItem[]>([]);
  const selectableStaffOptions = availableStaffOptions.filter((staff) => !state || staff.id !== state.staff.id);
  const staffAutoCompleteOptions = selectableStaffOptions.map((staff) => ({
    value: String(staff.id),
    name: formatSysStaffOptionLabel(staff),
  }));

  useEffect(() => {
    /** 加载员工当前主管关系。 */
    async function load() {
      if (!state) {
        return;
      }
      onClearError();
      const result = await onLoadSupervisors(state.staff.id);
      setItems(
        result.map((item) => ({
          supervisorId: item.supervisorId,
          supervisorLevel: item.supervisorLevel,
          orderNum: item.orderNum,
        })),
      );
    }
    void load();
  }, [state]);

  /** 提交领导关系保存。 */
  async function handleOk() {
    if (!state) {
      return;
    }
    await onSaveSupervisors(state.staff.id, items);
    onClose();
  }

  /** 更新指定主管关系行。 */
  function updateItem(index: number, next: SysStaffSupervisorMutationItem) {
    setItems((current) => current.map((item, currentIndex) => (currentIndex === index ? next : item)));
  }

  return (
    <Modal
      title={state ? `添加领导 - ${state.staff.realname || state.staff.username}` : "添加领导"}
      visible={Boolean(state)}
      onCancel={onClose}
      onOk={handleOk}
      okText="保存"
      cancelText="取消"
      className="sys-dept-user-modal sys-dept-user-supervisor-modal"
      unmountOnExit
    >
      {errorMessage ? <div className="sys-dept-user-error">{errorMessage}</div> : null}
      <div className="sys-dept-user-supervisor-list">
        {items.map((item, index) => (
          <div className="sys-dept-user-supervisor-item" key={`${item.supervisorId}-${index}`}>
            <label className="sys-dept-user-field">
              <span>领导</span>
              <AutoComplete
                allowClear
                data={staffAutoCompleteOptions}
                filterOption={filterAutoCompleteOption}
                placeholder="请选择领导"
                value={getStaffOptionLabel(selectableStaffOptions, item.supervisorId)}
                inputProps={{ "aria-label": `领导-${index + 1}` }}
                onChange={(value) => {
                  if (!value) {
                    updateItem(index, {
                      ...item,
                      supervisorId: 0,
                    });
                  }
                }}
                onSelect={(value) =>
                  updateItem(index, {
                    ...item,
                    supervisorId: Number(value),
                  })
                }
              />
            </label>
            <label className="sys-dept-user-field">
              <span>层级</span>
              <Input
                aria-label={`层级-${index + 1}`}
                value={String(item.supervisorLevel)}
                onChange={(value) =>
                  updateItem(index, {
                    ...item,
                    supervisorLevel: Number(value || 1),
                  })
                }
              />
            </label>
            <label className="sys-dept-user-field">
              <span>排序</span>
              <Input
                aria-label={`排序-${index + 1}`}
                value={String(item.orderNum)}
                onChange={(value) =>
                  updateItem(index, {
                    ...item,
                    orderNum: Number(value || 0),
                  })
                }
              />
            </label>
            <Button onClick={() => setItems((current) => current.filter((_, currentIndex) => currentIndex !== index))}>
              删除
            </Button>
          </div>
        ))}
        <div className="sys-dept-user-modal-actions">
          <Button
            onClick={() =>
              setItems((current) => [
                ...current,
                {
                  supervisorId: selectableStaffOptions[0]?.id ?? 0,
                  supervisorLevel: 1,
                  orderNum: current.length + 1,
                },
              ])
            }
          >
            新增领导
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** 按展示名和 ID 过滤自动补全选项。 */
function filterAutoCompleteOption(inputValue: string, option: { value?: string | number; extra?: { name?: string } }) {
  const keyword = inputValue.trim().toLowerCase();
  if (!keyword) {
    return true;
  }
  const text = `${option.extra?.name ?? ""} ${option.value ?? ""}`.toLowerCase();
  return text.includes(keyword);
}

/** 根据领导员工 ID 获取自动补全展示名称。 */
function getStaffOptionLabel(items: SysStaffItem[], staffId: number): string {
  const staff = items.find((item) => item.id === staffId);
  return staff ? formatSysStaffOptionLabel(staff) : "";
}
